import { useTranslation } from 'react-i18next'
import { formatCents } from '../../../lib/money'

interface VerticalRow {
  label: string
  amount: number
  pctOfIng: number | null
}

export function VerticalAnalysisTable({
  rows,
  showHeader = true,
}: {
  rows: VerticalRow[]
  /** false cuando el título ya lo pone quien lo envuelve (un <details>). */
  showHeader?: boolean
}) {
  const { t } = useTranslation('accounting')
  // Heurística: los conceptos cuyo label aparece como "subtotal" del waterfall.
  const isSubtotal = (label: string): boolean => {
    const lower = label.toLowerCase()
    return (
      lower.startsWith('margen') ||
      lower.startsWith('ebitda') ||
      lower.startsWith('ebit') ||
      lower.startsWith('resultado') ||
      lower.startsWith('beneficio')
    )
  }

  return (
    <section className="rounded-xl border border-[var(--border)] overflow-hidden">
      {showHeader && (
        <div className="px-4 py-3 bg-[var(--bg-input-soft)] border-b border-[var(--border)]">
          <h2 className="font-semibold t-primary">{t('vertical.title')}</h2>
          <p className="text-xs t-muted mt-0.5">{t('vertical.subtitle')}</p>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-input-soft)] t-muted">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t('vertical.concept')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('vertical.amount')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('vertical.pctIncome')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const subtotal = isSubtotal(r.label)
            return (
              <tr
                key={i}
                className={[
                  'border-t border-[var(--border)]/60',
                  subtotal ? 'bg-[var(--bg-hover)]' : '',
                ].join(' ')}
              >
                <td
                  className={[
                    'px-4 py-2',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {r.label}
                </td>
                <td
                  className={[
                    'px-4 py-2 text-right font-mono',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {formatCents(r.amount)}
                </td>
                <td
                  className={[
                    'px-4 py-2 text-right font-mono',
                    subtotal ? 'font-semibold t-primary' : 't-secondary',
                  ].join(' ')}
                >
                  {r.pctOfIng === null ? (
                    <span title={t('vertical.noIncomeTitle')} className="t-muted">
                      —
                    </span>
                  ) : (
                    `${r.pctOfIng.toFixed(1)}%`
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </section>
  )
}
